import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Download, FileText, Printer } from 'lucide-react';
import { Button, PageHeader, StatusBadge } from '@/components/ui';
import { cn } from '@/lib/utils';
import { getApiErrorMessage } from '@/lib/axios';
import { listEngins } from '@/services/engins';
import { listTiers } from '@/services/tiers';
import { listChantiers } from '@/services/chantiers';
import { getEntreprise, jourReposJs } from '@/services/parametres';
import {
  type EtatFiltres,
  type LigneEtat,
  type SensLocation,
  type SousTotalEtat,
  getEtatLocation,
  openEtatLocationPdf,
  pointerJour,
} from '@/services/etatsLocation';
import { toast } from '@/store/toastStore';
import { formatDZD } from '@/utils/algeria';
import { type PointageSortie, PointageForm, PopoverPointage } from './PointageForm';
import { FicheMensuelleModal } from './FicheMensuelleModal';

/* ------------------------------- Dates (mêmes conventions que le Pointage) ---- */
const pad = (n: number) => String(n).padStart(2, '0');
const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const addDays = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
/** Samedi qui ouvre la semaine contenant `d` (semaine du samedi au vendredi). */
const samediSemaine = (d: Date) => addDays(d, -(((d.getDay() - 6) + 7) % 7));
const MOIS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const JOUR_NOM = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
const JOUR_ABBR = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
const JOUR_LETTRE = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

const selectCls = 'h-9 w-full rounded-md border border-border bg-surface-secondary px-2 text-sm';
const n = (s: string | null | undefined) => Number(s || 0);
const round1 = (x: number) => Math.round(x * 10) / 10;

type Vue = 'jour' | 'semaine' | 'mois';
/** A3 — définitions canoniques du sens du flux, identiques partout dans l'ERP. */
const SENS: { key: SensLocation; label: string }[] = [
  { key: 'toutes', label: 'Les deux (présentés séparément)' },
  { key: 'sortante', label: 'Sortante — ma machine louée à un client (j’encaisse)' },
  { key: 'entrante', label: 'Entrante — machine d’un tiers que je loue (je paie)' },
];

/**
 * Une ligne de grille = UNE MACHINE dans UN SENS, tous ses contrats cumulés (A2).
 * Une machine louée deux fois dans le mois n'apparaît donc qu'une seule fois.
 */
interface LigneGrille {
  cle: string;
  documents: { id: string; modifiable: boolean }[];
  modifiable: boolean;
  tarifJour: number;
  engin: string;
  enginDetail: string;      // type · marque · modèle
  tiers: string[];          // clients (sortante) ou loueurs (entrante) concernés
  chauffeurs: Set<string>;  // conducteurs vus sur la période
  sens: 'SORTANTE' | 'ENTRANTE';
  jours: Record<string, LigneEtat[]>;   // ISO date → pointages du jour
  totalJours: number;
  totalHeures: number;
  totalNet: number;
  totalMarge: number;
  pointe: boolean;          // au moins une journée pointée sur la période
}

export function EtatsLocationPage() {
  // Pré-filtrage possible depuis « Location d'engins » (état d'UNE machine).
  const [params] = useSearchParams();
  const [vue, setVue] = useState<Vue>('semaine');
  const [cursor, setCursor] = useState(() => new Date());
  const [sens, setSens] = useState<SensLocation>((params.get('sens') as SensLocation) || 'toutes');
  const [enginId, setEnginId] = useState(params.get('engin_id') ?? '');
  const [tiersId, setTiersId] = useState('');
  const [chantierId, setChantierId] = useState('');
  // A2 — les locations sans aucun jour pointé sont masquées ; on peut les rappeler
  // pour démarrer un pointage sur un contrat tout neuf.
  const [afficherVides, setAfficherVides] = useState(false);
  const [ficheOuverte, setFicheOuverte] = useState(false);   // fiches de pointage mensuelles (une par machine)

  // Dates visibles selon la vue (mêmes règles que le module Pointage).
  const visibleDates = useMemo<Date[]>(() => {
    if (vue === 'jour') return [cursor];
    if (vue === 'semaine') { const s = samediSemaine(cursor); return Array.from({ length: 7 }, (_, i) => addDays(s, i)); }
    const y = cursor.getFullYear(), m = cursor.getMonth(), nb = new Date(y, m + 1, 0).getDate();
    return Array.from({ length: nb }, (_, i) => new Date(y, m, i + 1));
  }, [vue, cursor]);

  const filtres: EtatFiltres = {
    granularite: 'jour',            // on pivote côté écran ; le serveur reste la source des montants
    sens,
    engin_id: enginId || undefined,
    tiers_id: tiersId || undefined,
    chantier_id: chantierId || undefined,
    date_from: iso(visibleDates[0]),
    date_to: iso(visibleDates[visibleDates.length - 1]),
  };

  const { data: engins } = useQuery({ queryKey: ['engins', 'mini'], queryFn: () => listEngins({}) });
  const { data: tiers } = useQuery({ queryKey: ['tiers', 'all-mini'], queryFn: () => listTiers({ size: 200 }) });
  const { data: chantiers } = useQuery({ queryKey: ['chantiers', 'mini'], queryFn: () => listChantiers() });
  const { data: entreprise } = useQuery({ queryKey: ['entreprise'], queryFn: getEntreprise });
  const { data: etat, isLoading } = useQuery({ queryKey: ['etat-location', filtres], queryFn: () => getEtatLocation(filtres) });

  // A5 — jour de repos paramétré (Paramètres → Entreprise), vendredi par défaut.
  const jourReposIdx = jourReposJs(entreprise?.jour_repos_hebdo);
  const estRepos = (d: Date) => d.getDay() === jourReposIdx;

  // Pivot : UNE LIGNE PAR MACHINE ET PAR SENS (A2), tous contrats confondus.
  const lignes = useMemo<LigneGrille[]>(() => {
    const map = new Map<string, LigneGrille>();
    const clef = (enginIdRow: string, s: string) => `${enginIdRow}|${s}`;
    for (const d of etat?.documents ?? []) {
      const k = clef(d.engin_id, d.sens);
      const l = map.get(k);
      if (l) {
        l.documents.push({ id: d.document_id, modifiable: d.modifiable });
        l.modifiable = l.modifiable || d.modifiable;
        if (!l.tiers.includes(d.tiers_libelle)) l.tiers.push(d.tiers_libelle);
        continue;
      }
      map.set(k, {
        cle: k, documents: [{ id: d.document_id, modifiable: d.modifiable }],
        modifiable: d.modifiable, tarifJour: n(d.tarif_jour),
        engin: d.engin_libelle,
        enginDetail: [d.engin_categorie, d.engin_marque, d.engin_modele].filter(Boolean).join(' · '),
        tiers: [d.tiers_libelle], chauffeurs: new Set<string>(), sens: d.sens,
        jours: {}, totalJours: 0, totalHeures: 0, totalNet: 0, totalMarge: 0, pointe: false,
      });
    }
    for (const g of etat?.groupes ?? []) {
      for (const l of g.lignes) {
        const ligne = map.get(clef(l.engin_id, l.sens));
        if (!ligne) continue;
        (ligne.jours[l.date] ||= []).push(l);
        if (l.chauffeur) ligne.chauffeurs.add(l.chauffeur);
        ligne.totalJours += n(l.journees);
        ligne.totalHeures += n(l.heures_sup);
        ligne.totalNet += n(l.net);
        ligne.totalMarge += n(l.marge);
        ligne.pointe = ligne.pointe || n(l.journees) > 0 || n(l.heures_sup) > 0;
      }
    }
    return [...map.values()]
      .filter((l) => afficherVides || l.pointe)
      .sort((a, b) => a.engin.localeCompare(b.engin) || a.sens.localeCompare(b.sens));
  }, [etat, afficherVides]);

  const masquees = (etat?.documents.length ?? 0) > 0 && !afficherVides
    ? new Set((etat?.documents ?? []).map((d) => `${d.engin_id}|${d.sens}`)).size - lignes.length
    : 0;

  // Saisie directe en grille : clic sur une case → menu → upsert de la journée.
  const qc = useQueryClient();
  const [menu, setMenu] = useState<{ ligne: LigneGrille; date: string; x: number; y: number } | null>(null);
  /** Contrat qui reçoit la journée : celui déjà pointé ce jour-là, sinon le premier ouvert. */
  const documentPour = (l: LigneGrille, date: string): string | null => {
    const deja = (l.jours[date] ?? []).find((x) => x.statut === 'BROUILLON');
    return deja ? deja.document_id : (l.documents.find((d) => d.modifiable)?.id ?? null);
  };
  const pointerM = useMutation({
    mutationFn: (v: { ligne: LigneGrille; date: string; sortie: PointageSortie }) => {
      const doc = documentPour(v.ligne, v.date);
      if (!doc) return Promise.reject(new Error('Aucun contrat ouvert pour cette machine.'));
      return pointerJour(v.ligne.sens, doc, {
        date: v.date, journees: v.sortie.journees, heures_sup: v.sortie.heures_sup,
        heure_debut: v.sortie.heure_debut, heure_fin: v.sortie.heure_fin, motif_perte: v.sortie.motif_perte,
      });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['etat-location'] }); setMenu(null); },
    onError: (e) => { toast.error(getApiErrorMessage(e)); setMenu(null); },
  });

  const navJour = (d: number) => setCursor((c) => addDays(c, d));
  const navSemaine = (d: number) => setCursor((c) => addDays(c, d * 7));
  const navMois = (d: number) => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + d, 1));

  const imprimer = async (mode: 'open' | 'download') => {
    try {
      // L'état imprimé reflète la VUE active (jour / semaine / mois) et la période affichée.
      await openEtatLocationPdf({ ...filtres, granularite: vue === 'jour' ? 'jour' : vue === 'semaine' ? 'semaine' : 'mois' }, mode);
    } catch (e) { toast.error(getApiErrorMessage(e)); }
  };

  const sat = samediSemaine(cursor);
  const masque = !!etat?.montants_masques;   // pas de droit « tarifs » : pointages visibles, montants non

  return (
    <>
      <PageHeader
        title="États de location"
        subtitle="Pointage des engins loués — vues journalière, hebdomadaire et mensuelle. Imprimable pour le client ou le loueur."
        breadcrumbs={[{ label: 'Advanced Works ERP' }, { label: 'Parc & matériel', to: '/engins' }, { label: 'États' }]}
      />

      {/* Barre de vue + navigation de période (même ergonomie que le Pointage employés) */}
      <div className="mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-border bg-surface-secondary p-3 shadow-sm">
        <div className="inline-flex rounded-md border border-border p-0.5">
          {(['jour', 'semaine', 'mois'] as Vue[]).map((v) => (
            <button
              key={v}
              onClick={() => setVue(v)}
              className={cn('rounded px-3 py-1.5 text-sm font-medium transition-colors',
                vue === v ? 'bg-brand-accent text-brand-accent-contrast' : 'text-content-secondary hover:bg-surface-hover')}
            >
              {v === 'jour' ? 'Journalier' : v === 'semaine' ? 'Hebdomadaire' : 'Mensuel'}
            </button>
          ))}
        </div>

        {vue === 'mois' && (
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={() => navMois(-1)} aria-label="Mois précédent"><ChevronLeft className="size-4" /></Button>
            <span className="min-w-[140px] text-center text-sm font-semibold text-content-primary">{MOIS[cursor.getMonth()]} {cursor.getFullYear()}</span>
            <Button variant="secondary" size="sm" onClick={() => navMois(1)} aria-label="Mois suivant"><ChevronRight className="size-4" /></Button>
            <Button variant="secondary" size="sm" onClick={() => setCursor(new Date())}>Ce mois-ci</Button>
          </div>
        )}
        {vue === 'semaine' && (
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={() => navSemaine(-1)} aria-label="Semaine précédente"><ChevronLeft className="size-4" /></Button>
            <span className="min-w-[180px] text-center text-sm font-semibold text-content-primary">
              Sem. {pad(sat.getDate())}/{pad(sat.getMonth() + 1)} → {pad(addDays(sat, 6).getDate())}/{pad(addDays(sat, 6).getMonth() + 1)}
            </span>
            <Button variant="secondary" size="sm" onClick={() => navSemaine(1)} aria-label="Semaine suivante"><ChevronRight className="size-4" /></Button>
            <Button variant="secondary" size="sm" onClick={() => setCursor(new Date())}>Cette semaine</Button>
          </div>
        )}
        {vue === 'jour' && (
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={() => navJour(-1)} aria-label="Jour précédent"><ChevronLeft className="size-4" /></Button>
            <span className="min-w-[210px] text-center text-sm font-semibold text-content-primary">
              {JOUR_NOM[cursor.getDay()]} {pad(cursor.getDate())}/{pad(cursor.getMonth() + 1)}/{cursor.getFullYear()} {estRepos(cursor) ? '· repos' : ''}
            </span>
            <Button variant="secondary" size="sm" onClick={() => navJour(1)} aria-label="Jour suivant"><ChevronRight className="size-4" /></Button>
            <Button variant="secondary" size="sm" onClick={() => setCursor(new Date())}>Aujourd'hui</Button>
          </div>
        )}

        <div className="ml-auto flex flex-wrap items-center gap-2">
          {/* Sans montants : accessible aussi au chef de chantier (le PDF de l'état, lui, expose des tarifs). */}
          <Button variant="secondary" size="sm" onClick={() => setFicheOuverte(true)}
            title="Un tableau par machine : tous les jours du mois, heures sup, heures perdues et gasoil">
            <FileText className="size-4" /> Fiches de pointage (par machine)
          </Button>
          {!masque && (
            <>
              <Button size="sm" onClick={() => imprimer('open')}><Printer className="size-4" /> Imprimer l'état</Button>
              <Button variant="secondary" size="sm" onClick={() => imprimer('download')} aria-label="Télécharger en PDF"><Download className="size-4" /></Button>
            </>
          )}
        </div>
      </div>

      {/* Filtres */}
      <div className="mb-3 grid grid-cols-2 gap-3 rounded-lg border border-border bg-surface-secondary p-3 sm:grid-cols-4">
        <label className="text-xs text-content-secondary">Type de location
          <select className={selectCls} value={sens} onChange={(e) => setSens(e.target.value as SensLocation)}>
            {SENS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </label>
        <label className="text-xs text-content-secondary">Engin
          <select className={selectCls} value={enginId} onChange={(e) => setEnginId(e.target.value)}>
            <option value="">Tous</option>
            {engins?.items.map((x) => <option key={x.id} value={x.id}>{x.code} · {x.designation}</option>)}
          </select>
        </label>
        <label className="text-xs text-content-secondary">Client / Loueur
          <select className={selectCls} value={tiersId} onChange={(e) => setTiersId(e.target.value)}>
            <option value="">Tous</option>
            {tiers?.items.map((t) => <option key={t.id} value={t.id}>{t.raison_sociale}</option>)}
          </select>
        </label>
        <label className="text-xs text-content-secondary">Chantier
          <select className={selectCls} value={chantierId} onChange={(e) => setChantierId(e.target.value)}>
            <option value="">Tous</option>
            {chantiers?.items.map((c) => <option key={c.id} value={c.id}>{c.code} · {c.nom}</option>)}
          </select>
        </label>
      </div>

      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-content-muted">
          Semaine du samedi au vendredi · repos le {JOUR_NOM[jourReposIdx].toLowerCase()} (modifiable dans Paramètres → Entreprise).
          Une machine = une ligne, tous ses contrats cumulés. <StatusBadge status="done" label="S" /> sortante (le client me paie) ·{' '}
          <StatusBadge status="working" label="E" /> entrante (je paie le loueur).
        </p>
        <label className="flex shrink-0 items-center gap-2 text-xs text-content-secondary">
          <input type="checkbox" className="size-3.5 accent-current" checked={afficherVides}
                 onChange={(e) => setAfficherVides(e.target.checked)} />
          Afficher les locations sans jour pointé{masquees > 0 ? ` (${masquees})` : ''}
        </label>
      </div>

      {isLoading ? (
        <div className="rounded-lg border border-border bg-surface-secondary p-12 text-center text-sm text-content-muted">Chargement…</div>
      ) : vue === 'jour' ? (
        <VueJournaliere date={visibleDates[0]} lignes={lignes} masque={masque} />
      ) : (
        <Grille vue={vue} dates={visibleDates} lignes={lignes} estRepos={estRepos} masque={masque}
                onCase={(ligne, date, x, y) => setMenu({ ligne, date, x, y })} />
      )}

      {/* Formulaire de pointage d'une journée (clic sur une case de la grille) */}
      {menu && (
        <PopoverPointage x={menu.x} y={menu.y} onClose={() => setMenu(null)}>
          <PointageForm
            key={`${menu.ligne.cle}-${menu.date}`}
            titre={`${menu.ligne.engin.split(' · ')[0]} · ${menu.date.slice(8, 10)}/${menu.date.slice(5, 7)}`}
            enRepos={estRepos(new Date(`${menu.date}T00:00:00`))}
            horaires={menu.ligne.sens === 'ENTRANTE'}
            gasoil={null}
            initial={(() => {
              const items = menu.ligne.jours[menu.date] ?? [];
              const j = items.reduce((a, x) => a + n(x.journees), 0);
              const h = items.reduce((a, x) => a + n(x.heures_sup), 0);
              if (items.length === 0 || j === 0) return {};
              return { presence: j === 0.5 ? 'DEMI' as const : 'PLEINE' as const, heuresSup: h ? String(h) : '' };
            })()}
            enCours={pointerM.isPending}
            onEnregistrer={(sortie) => pointerM.mutate({ ligne: menu.ligne, date: menu.date, sortie })}
            onEffacer={() => pointerM.mutate({ ligne: menu.ligne, date: menu.date, sortie: { journees: '0', heures_sup: '0' } })}
          />
        </PopoverPointage>
      )}

      {/* A1 — RÉCAPITULATIF : deux flux opposés, jamais additionnés. */}
      {etat && !masque && <Recapitulatif etat={etat} />}

      {/* Fiches de pointage mensuelles : une page A4 par machine du périmètre filtré ci-dessus */}
      {ficheOuverte && (
        <FicheMensuelleModal
          titre={(() => {
            const machine = engins?.items.find((x) => x.id === enginId);
            if (machine) return `${machine.code} · ${machine.designation}`;
            const parties = ['Toutes les machines'];
            if (sens !== 'toutes') parties.push(sens === 'sortante' ? 'location sortante' : 'location entrante');
            const t = tiers?.items.find((x) => x.id === tiersId);
            if (t) parties.push(t.raison_sociale);
            const c = chantiers?.items.find((x) => x.id === chantierId);
            if (c) parties.push(`${c.code} · ${c.nom}`);
            return parties.join(' — ');
          })()}
          perimetre={{ sens, engin_id: enginId || undefined, tiers_id: tiersId || undefined, chantier_id: chantierId || undefined }}
          moisInitial={cursor}
          onClose={() => setFicheOuverte(false)}
        />
      )}
    </>
  );
}

/* ------------------------- Récapitulatif encaisser / payer ------------------- */
const aDuContenu = (s: SousTotalEtat | undefined) => !!s && (n(s.brut) !== 0 || n(s.journees) !== 0);

function Recapitulatif({ etat }: { etat: { sortante: SousTotalEtat; entrante: SousTotalEtat; position: string; cout_entretien: string } }) {
  const s = aDuContenu(etat.sortante), e = aDuContenu(etat.entrante);
  if (!s && !e) return null;
  return (
    <div className="mt-4 flex flex-wrap justify-end gap-3">
      {s && (
        <BlocFlux
          titre="À ENCAISSER — clients"
          aide="Mes machines louées à des clients (revenu)."
          total={etat.sortante}
          libelleFinal="NET À ENCAISSER"
          tonalite="done"
          entretien={n(etat.cout_entretien)}
        />
      )}
      {e && (
        <BlocFlux
          titre="À PAYER — loueurs"
          aide="Machines de tiers que je loue pour mes chantiers (dépense)."
          total={etat.entrante}
          libelleFinal="NET À PAYER"
          tonalite="working"
        />
      )}
      {s && e && (
        <div className="w-full max-w-sm rounded-lg border border-status-stuck/40 bg-status-stuck/5 p-4 text-sm">
          <div className="flex items-center justify-between font-bold">
            <span className="text-content-primary">Solde de location (position)</span>
            <span className="tabular-nums text-content-primary">{formatDZD(n(etat.position))}</span>
          </div>
          <p className="mt-1.5 text-[11px] leading-snug text-content-muted">
            À encaisser − à payer. C'est une <b>position de trésorerie</b>, pas un montant de facture :
            ne la remettez ni au client ni au loueur.
          </p>
        </div>
      )}
    </div>
  );
}

function BlocFlux({ titre, aide, total, libelleFinal, tonalite, entretien }: {
  titre: string; aide: string; total: SousTotalEtat; libelleFinal: string;
  tonalite: 'done' | 'working'; entretien?: number;
}) {
  return (
    <div className="w-full max-w-sm rounded-lg border border-border bg-surface-secondary p-4 text-sm">
      <div className="mb-2 flex items-center gap-2">
        <StatusBadge status={tonalite} label={tonalite === 'done' ? 'S' : 'E'} />
        <span className="font-semibold text-content-primary">{titre}</span>
      </div>
      <p className="mb-2 text-[11px] text-content-muted">{aide}</p>
      <Row label="Total brut" value={formatDZD(n(total.brut))} />
      <Row label="− Gasoil déduit" value={`− ${formatDZD(n(total.deduction_gasoil))}`} tone="stuck" />
      <Row label="− Avances de la période" value={`− ${formatDZD(n(total.avances))}`} tone="stuck" />
      <div className="mt-2 flex items-center justify-between border-t border-border pt-2 text-base font-bold">
        <span>{libelleFinal}</span>
        <span className="tabular-nums text-brand-primary">{formatDZD(n(total.solde))}</span>
      </div>
      {/* Location entrante : gasoil que J'AI fourni = mes frais (déduit ou non), hors net à payer. */}
      {tonalite === 'working' && n(total.gasoil_montant) > 0 && (
        <p className="mt-2 border-t border-border pt-2 text-[11px] leading-snug text-content-muted">
          Gasoil fourni par moi : <b>{n(total.gasoil_litres)} L</b> · <b>{formatDZD(n(total.gasoil_montant))}</b> de frais
          (informatif — seule la part « déduite » ci-dessus réduit ce que je dois au loueur).
        </p>
      )}
      {/* A4 — rentabilité de la location : net − gasoil que je fournis. */}
      {tonalite === 'done' && (
        <div className="mt-2 space-y-0.5 border-t border-border pt-2">
          <Row label="− Gasoil à ma charge" value={`− ${formatDZD(n(total.cout_impute))}`} tone="stuck" />
          <div className="flex items-center justify-between font-semibold">
            <span className="text-content-secondary">Marge de l'opération</span>
            <span className={cn('tabular-nums', n(total.marge) >= 0 ? 'text-status-done' : 'text-status-stuck')}>
              {formatDZD(n(total.marge))}
            </span>
          </div>
          {!!entretien && (
            <p className="pt-1 text-[11px] leading-snug text-content-muted">
              Entretien de la période : {formatDZD(entretien)} — indicatif (coût de possession),
              <b> non déduit</b> de la marge.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/* --------------------------------- Grille hebdo / mensuelle ------------------ */
function Grille({ vue, dates, lignes, estRepos, masque, onCase }: {
  vue: Vue; dates: Date[]; lignes: LigneGrille[]; estRepos: (d: Date) => boolean; masque: boolean;
  onCase: (ligne: LigneGrille, date: string, x: number, y: number) => void;
}) {
  const wide = vue === 'mois';  // mensuel = grille compacte ; hebdomadaire = cases larges détaillées

  if (lignes.length === 0) {
    return <div className="rounded-lg border border-border bg-surface-secondary p-12 text-center text-sm text-content-muted">
      Aucune journée pointée sur cette période. Cochez « Afficher les locations sans jour pointé »
      pour démarrer le pointage d'un contrat, ou ouvrez la machine depuis « Location d'engins ».
    </div>;
  }

  // Totaux par colonne (jour).
  const totalJour = (d: Date) => lignes.reduce(
    (acc, l) => acc + (l.jours[iso(d)] ?? []).reduce((s, x) => s + n(x.journees), 0), 0);
  // A1 — les totaux de bas de grille restent séparés par sens.
  const parSens = (['SORTANTE', 'ENTRANTE'] as const)
    .map((s) => ({ sens: s, lignes: lignes.filter((l) => l.sens === s) }))
    .filter((x) => x.lignes.length > 0);

  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-surface-secondary shadow-sm">
      <table className={cn('border-collapse text-sm', wide ? 'w-full' : 'min-w-full')}>
        <thead>
          <tr className="border-b border-border bg-surface-primary/40">
            <th className={cn('sticky left-0 z-10 bg-surface-secondary px-3 py-2 text-left text-[11px] font-semibold uppercase text-content-muted',
              wide ? 'min-w-[210px]' : 'w-[240px]')}>Machine ({lignes.length})</th>
            {dates.map((d) => (
              <th key={iso(d)} className={cn('py-1.5 text-center font-semibold',
                wide ? 'w-8 px-0 text-[11px]' : 'min-w-[76px] px-1 text-xs',
                estRepos(d) ? 'bg-status-stuck/10 text-status-stuck' : 'text-content-secondary')}>
                {wide ? (
                  <>
                    <div>{JOUR_LETTRE[d.getDay()]}{estRepos(d) ? ' 🌙' : ''}</div>
                    <div className="text-content-muted">{d.getDate()}</div>
                  </>
                ) : (
                  <>
                    <div className="text-[13px]">{JOUR_ABBR[d.getDay()]}{estRepos(d) ? ' · repos' : ''}</div>
                    <div className="text-[11px] font-normal text-content-muted">{pad(d.getDate())}/{pad(d.getMonth() + 1)}</div>
                  </>
                )}
              </th>
            ))}
            <th className="bg-brand-primary/10 px-2 py-2 text-center text-[11px] font-semibold uppercase text-brand-primary">Jours</th>
            <th className="bg-status-done/10 px-2 py-2 text-center text-[11px] font-semibold uppercase text-status-done">H.&nbsp;sup</th>
            {!masque && <th className="whitespace-nowrap bg-surface-primary/60 px-2 py-2 text-right text-[11px] font-semibold uppercase text-content-muted">Net</th>}
            {!masque && <th className="whitespace-nowrap bg-surface-primary/60 px-2 py-2 text-right text-[11px] font-semibold uppercase text-content-muted">Marge</th>}
          </tr>
        </thead>
        <tbody>
          {lignes.map((l) => (
            <tr key={l.cle} className="border-b border-border/60 last:border-0">
              <td className="sticky left-0 z-10 bg-surface-secondary px-3 py-1.5">
                <div className="flex items-center gap-1.5">
                  <StatusBadge status={l.sens === 'SORTANTE' ? 'done' : 'working'} label={l.sens === 'SORTANTE' ? 'S' : 'E'} />
                  <span className="truncate font-medium text-content-primary">{l.engin}</span>
                  {!l.modifiable && <span title="Location validée — lecture seule" className="text-content-muted">🔒</span>}
                  {l.documents.length > 1 && (
                    <span title={`${l.documents.length} contrats cumulés sur la période`}
                          className="rounded bg-surface-primary/60 px-1 text-[10px] text-content-muted">×{l.documents.length}</span>
                  )}
                </div>
                {l.enginDetail && <div className="truncate text-[10px] text-content-muted">{l.enginDetail}</div>}
                <div className="truncate text-[11px] text-content-muted">{l.tiers.join(', ')}</div>
                {l.chauffeurs.size > 0 && (
                  <div className="truncate text-[10px] text-content-secondary">👤 {[...l.chauffeurs].join(', ')}</div>
                )}
              </td>
              {dates.map((d) => {
                const items = l.jours[iso(d)] ?? [];
                const j = items.reduce((s, x) => s + n(x.journees), 0);
                const h = items.reduce((s, x) => s + n(x.heures_sup), 0);
                const net = items.reduce((s, x) => s + n(x.net), 0);
                const vide = j === 0 && h === 0;
                const cliquable = l.modifiable;
                const repos = estRepos(d);
                return (
                  <td
                    key={iso(d)}
                    onClick={cliquable ? (ev) => onCase(l, iso(d), ev.clientX, ev.clientY) : undefined}
                    title={cliquable
                      ? (repos ? 'Jour de repos — cliquer pour pointer quand même' : 'Cliquer pour pointer cette journée')
                      : 'Location validée — lecture seule'}
                    className={cn('text-center', wide ? 'p-0.5' : 'p-1',
                      vide && repos && 'bg-status-stuck/5',
                      cliquable && 'cursor-pointer hover:bg-brand-primary/10')}
                  >
                    {vide ? <span className="text-content-muted/50">·</span> : (
                      <div
                        title={`${iso(d)} · ${j} j${h ? ` + ${h} h sup` : ''} · ${formatDZD(net)}${repos ? ' · travaillé un jour de repos' : ''}`}
                        className={cn('mx-auto rounded font-semibold tabular-nums',
                          wide ? 'px-0.5 py-0.5 text-[10px]' : 'px-1.5 py-1 text-xs',
                          // A5 — une journée pointée un jour de repos est signalée, pas interdite.
                          repos && 'ring-1 ring-status-stuck/60',
                          l.sens === 'SORTANTE' ? 'bg-status-done/15 text-status-done' : 'bg-brand-primary/15 text-brand-primary')}
                      >
                        <div>{j === 0.5 ? '½' : j || '—'}</div>
                        {h > 0 && <div className={cn('font-normal text-content-secondary', wide ? 'text-[8px]' : 'text-[10px]')}>+{round1(h)}h</div>}
                      </div>
                    )}
                  </td>
                );
              })}
              <td className="bg-brand-primary/5 px-2 text-center font-semibold tabular-nums text-brand-primary">{round1(l.totalJours) || ''}</td>
              <td className="bg-status-done/5 px-2 text-center font-semibold tabular-nums text-status-done">{l.totalHeures ? `${round1(l.totalHeures)}h` : ''}</td>
              {!masque && <td className="whitespace-nowrap px-2 text-right font-semibold tabular-nums text-content-primary">{formatDZD(l.totalNet)}</td>}
              {!masque && <td className={cn('whitespace-nowrap px-2 text-right font-semibold tabular-nums',
                l.sens === 'SORTANTE' ? 'text-status-done' : 'text-content-muted')}>
                {l.sens === 'SORTANTE' ? formatDZD(l.totalMarge) : '—'}
              </td>}
            </tr>
          ))}
          {/* Totaux : une ligne PAR SENS — on n'additionne pas un encaissement et un paiement. */}
          {parSens.map(({ sens: s, lignes: ls }) => (
            <tr key={s} className="border-t-2 border-border bg-surface-primary/50 text-xs font-semibold">
              <td className="sticky left-0 z-10 bg-surface-primary/50 px-3 py-1.5 uppercase text-content-muted">
                {s === 'SORTANTE' ? 'Total à encaisser' : 'Total à payer'}
              </td>
              {dates.map((d) => {
                const t = ls.reduce((acc, l) => acc + (l.jours[iso(d)] ?? []).reduce((x, y) => x + n(y.journees), 0), 0);
                return <td key={iso(d)} className="px-1 py-1.5 text-center tabular-nums text-content-secondary">{t ? round1(t) : ''}</td>;
              })}
              <td className="bg-brand-primary/10 px-2 text-center tabular-nums text-brand-primary">
                {round1(ls.reduce((acc, l) => acc + l.totalJours, 0))}
              </td>
              <td className="bg-status-done/10 px-2 text-center tabular-nums text-status-done">
                {round1(ls.reduce((acc, l) => acc + l.totalHeures, 0)) || ''}
              </td>
              {!masque && <td className="whitespace-nowrap px-2 text-right tabular-nums text-content-primary">
                {formatDZD(ls.reduce((acc, l) => acc + l.totalNet, 0))}
              </td>}
              {!masque && <td className="whitespace-nowrap px-2 text-right tabular-nums text-status-done">
                {s === 'SORTANTE' ? formatDZD(ls.reduce((acc, l) => acc + l.totalMarge, 0)) : '—'}
              </td>}
            </tr>
          ))}
          {/* Volumétrie toutes machines confondues (jours seulement : additionner des jours a un sens). */}
          {parSens.length > 1 && (
            <tr className="border-t border-border bg-surface-primary/30 text-[11px] text-content-muted">
              <td className="sticky left-0 z-10 bg-surface-primary/30 px-3 py-1 uppercase">Jours machine / jour</td>
              {dates.map((d) => {
                const t = totalJour(d);
                return <td key={iso(d)} className="px-1 py-1 text-center tabular-nums">{t ? round1(t) : ''}</td>;
              })}
              <td className="px-2 text-center tabular-nums">{round1(lignes.reduce((s, l) => s + l.totalJours, 0))}</td>
              <td colSpan={masque ? 1 : 3} />
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

/* --------------------------------- Vue journalière (cartes) ------------------ */
function VueJournaliere({ date, lignes, masque }: { date: Date; lignes: LigneGrille[]; masque: boolean }) {
  const jour = iso(date);
  const cartes = lignes
    .map((l) => ({ ligne: l, items: l.jours[jour] ?? [] }))
    .filter((c) => c.items.length > 0);

  if (cartes.length === 0) {
    return <div className="rounded-lg border border-border bg-surface-secondary p-12 text-center text-sm text-content-muted">
      Aucun pointage de location ce jour.
    </div>;
  }

  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
      {cartes.map(({ ligne, items }) => {
        const j = items.reduce((s, x) => s + n(x.journees), 0);
        const h = items.reduce((s, x) => s + n(x.heures_sup), 0);
        const brut = items.reduce((s, x) => s + n(x.brut), 0);
        const ded = items.reduce((s, x) => s + n(x.deduction_gasoil), 0);
        const net = items.reduce((s, x) => s + n(x.net), 0);
        const marge = items.reduce((s, x) => s + n(x.marge), 0);
        const it = items[0];
        return (
          <div key={ligne.cle} className="rounded-lg border border-border bg-surface-secondary p-4 shadow-sm">
            <div className="mb-2 flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate font-semibold text-content-primary">{ligne.engin}</p>
                {ligne.enginDetail && <p className="truncate text-[11px] text-content-muted">{ligne.enginDetail}</p>}
                <p className="truncate text-xs text-content-muted">{ligne.tiers.join(', ')}{it.chantier_libelle ? ` · ${it.chantier_libelle}` : ''}</p>
              </div>
              <StatusBadge status={ligne.sens === 'SORTANTE' ? 'done' : 'working'}
                label={ligne.sens === 'SORTANTE' ? 'Sortante' : 'Entrante'} />
            </div>
            <div className="grid grid-cols-3 gap-2 text-center text-sm">
              <Case label="Journées" value={j === 0.5 ? '½' : String(round1(j))} />
              <Case label="H. sup" value={h ? `${round1(h)} h` : '—'} />
              <Case label="Gasoil" value={n(it.gasoil_litres) ? `${n(it.gasoil_litres)} L` : (!masque && n(it.gasoil_montant) ? formatDZD(n(it.gasoil_montant)) : '—')} />
            </div>
            {!masque && <div className="mt-3 space-y-0.5 border-t border-border pt-2 text-sm">
              <Row label="Brut" value={formatDZD(brut)} />
              {ded > 0 && <Row label="− Gasoil déduit" value={`− ${formatDZD(ded)}`} tone="stuck" />}
              <div className="flex items-center justify-between pt-1 font-bold">
                <span>{ligne.sens === 'SORTANTE' ? 'Net à encaisser (jour)' : 'Net à payer (jour)'}</span>
                <span className="tabular-nums text-brand-primary">{formatDZD(net)}</span>
              </div>
              {ligne.sens === 'SORTANTE' && (
                <div className="flex items-center justify-between text-xs">
                  <span className="text-content-secondary">dont marge</span>
                  <span className="tabular-nums text-status-done">{formatDZD(marge)}</span>
                </div>
              )}
            </div>}
            {it.jour_repos && (
              <p className="mt-2 text-xs text-status-stuck">Journée travaillée un jour de repos.</p>
            )}
            {it.chauffeur && <p className="mt-2 text-xs text-content-muted">Chauffeur : {it.chauffeur}</p>}
          </div>
        );
      })}
    </div>
  );
}

function Case({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-surface-primary/50 px-2 py-1.5">
      <p className="text-[10px] uppercase tracking-wide text-content-muted">{label}</p>
      <p className="font-semibold tabular-nums text-content-primary">{value}</p>
    </div>
  );
}

function Row({ label, value, tone }: { label: string; value: string; tone?: 'stuck' }) {
  return (
    <div className="flex items-center justify-between py-0.5">
      <span className="text-content-secondary">{label}</span>
      <span className={cn('tabular-nums', tone === 'stuck' ? 'text-status-stuck' : 'text-content-primary')}>{value}</span>
    </div>
  );
}
